import {urlB64ToUint8Array} from 'js-libs/sweetcommons/base64';
import {PUSH_API_URL} from 'js-libs/sweetcommons/urls';

import {postJson} from 'app/utils/request';

import {sendSentryError} from './sentry';

// proxy to simplify the browser push notification management/registration
export class WebPushNotifications {
  static isSupported(): boolean {
    return (
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      'Notification' in window
    );
  }

  static requestPermission(): Promise<NotificationPermission> {
    return new Promise((resolve) => {
      return window.Notification.requestPermission().then(
        (permission: NotificationPermission) => resolve(permission)
      );
    });
  }

  static canAskForPermission(): boolean {
    return window.Notification.permission === 'default';
  }

  static permissionIsGranted(): boolean {
    return window.Notification.permission === 'granted';
  }

  static permissionIsDenied(): boolean {
    return window.Notification.permission === 'denied';
  }

  static runOnPermissionChange(func: () => void): void {
    if (navigator.permissions != null) {
      navigator.permissions
        .query({
          name: 'notifications',
        })
        .then((permission) => {
          permission.onchange = func;
        });
    }
  }

  static subscribeToWebPushMessages(
    wsRegistration: ServiceWorkerRegistration,
    applicationServerKey: string
  ): void | Promise<unknown> {
    // safari service-workers doesnt support standard push notifications
    if (wsRegistration.pushManager === undefined) {
      return;
    }

    return wsRegistration.pushManager
      .getSubscription()
      .then((subscription: null | PushSubscription) => {
        if (
          subscription != null &&
          !keysAreEqual(
            subscription?.options.applicationServerKey,
            applicationServerKey
          )
        ) {
          return subscription.unsubscribe();
        }
        return true;
      })
      .then(
        () => {
          wsRegistration.pushManager
            .subscribe({
              userVisibleOnly: true,
              applicationServerKey: urlB64ToUint8Array(applicationServerKey),
            })
            .then(
              (pushSubscription) => {
                const subscription_info = pushSubscription.toJSON();
                postJson(PUSH_API_URL, {
                  subscription_info,
                }).catch(({data, message}) => {
                  const error = new Error(
                    'Failed to subscribe to push notifications due to malformed subscription data'
                  );
                  try {
                    sendSentryError(error, {
                      extra: {
                        context: 'wsRegistration.malformed',
                        data: data.subscription_info,
                        message,
                      },
                    });
                  } catch (err) {
                    // we are only interested in malformed nature of the push subscription
                  }
                });
              },
              (err) => {
                sendSentryError(err, {
                  extra: {
                    context: 'wsRegistration.pushManager.subscribe',
                  },
                });
              }
            );
        },
        (err) =>
          sendSentryError(err, {
            extra: {
              context: 'wsRegistration.pushManager.getSubscription',
            },
          })
      );
  }
}

function keysAreEqual(
  currentKey: undefined | null | ArrayBuffer,
  serverKey: string
): boolean {
  if (currentKey == null) {
    return false;
  }

  const applicationServerKey: Uint8Array = urlB64ToUint8Array(serverKey);
  const currentKeyAsArray = new Uint8Array(currentKey);

  if (currentKeyAsArray.length !== applicationServerKey.length) {
    return false;
  }

  for (let i = 0; i < currentKeyAsArray.length; i++) {
    // bails out on first different byte found
    if (currentKeyAsArray[i] !== applicationServerKey[i]) {
      return false;
    }
  }

  return true;
}
